import { useEffect, useRef, useState } from 'react';
import { useSocket } from './hooks/useSocket';
import LivestreamDisplay from './pages/LivestreamDisplay';
import LivestreamPublisher from './pages/LivestreamPublisher';
import QRScanner from './pages/QRScanner';
import Standby from './pages/Standby';
import AlarmReceived from './pages/AlarmReceived';
import EnrouteView from './pages/EnrouteView';
import './App.css';

type AppPage = 'scanner' | 'standby' | 'alarm' | 'enroute' | 'livestream' | 'livestream-display';

export default function App() {
  const { socket, isConnected } = useSocket('participant');
  const [currentPage, setCurrentPage] = useState<AppPage>('scanner');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [unitName, setUnitName] = useState<string | null>(null);
  const [crewName, setCrewName] = useState<string | null>(null);
  const [currentAlarm, setCurrentAlarm] = useState<any>(null);
  const [pendingAlarmId, setPendingAlarmId] = useState<string | null>(null);
  const [unitStatus, setUnitStatus] = useState<string>('standby');
  const alarmAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingAlarmPlaybackRef = useRef(false);

  const resolveDispatchedUnitNames = async (dispatchedUnits: unknown, currentSessionId: string) => {
    const unitIds = Array.isArray(dispatchedUnits)
      ? dispatchedUnits.map((value: unknown) => String(value || '').trim()).filter((value: string) => value.length > 0)
      : [];

    if (unitIds.length === 0) return [];

    try {
      const response = await fetch(`/api/sessions/${currentSessionId}/participants`);
      if (!response.ok) {
        return unitIds;
      }

      const participants = await response.json();
      const unitNamesById = new Map(
        (Array.isArray(participants) ? participants : []).map((participant: any) => [String(participant.id || ''), String(participant.unitName || participant.id || '')])
      );

      return unitIds.map((unitId: string) => unitNamesById.get(unitId) || unitId);
    } catch {
      return unitIds;
    }
  };

  const loadAlarmContext = async (alarmId: string) => {
    const response = await fetch(`/api/dispatch/${alarmId}`);
    if (!response.ok) {
      throw new Error(`Alarm load failed with status ${response.status}`);
    }

    const { medizinischesHauptproblem: _medizinischesHauptproblem, ...alarm } = await response.json();
    setSessionId(alarm.sessionId);
    setPendingAlarmId(alarm.id);
  const dispatchedUnitNames = await resolveDispatchedUnitNames(alarm.dispatchedUnits, alarm.sessionId);

    return {
      id: alarm.id,
      timestamp: alarm.dispatchedAt,
      sessionId: alarm.sessionId,
      einsatzcode: alarm.einsatzcode,
      mpdsProtocol: alarm.mpdsProtocol,
      mpdsDistinctDeterminant: alarm.mpdsDistinctDeterminant,
      callerName: alarm.callerName,
      callerPhone: alarm.callerPhone,
      location: {
        lat: alarm.latitude,
        lng: alarm.longitude,
        address: alarm.address,
        gemeinde: alarm.gemeinde,
        bezirk: alarm.bezirk,
      },
      dispatchedUnits: dispatchedUnitNames,
      funkgruppe: alarm.funkgruppe,
      alarmText: alarm.alarmText,
      sondertext: alarm.sondertext,
      additionalResources: alarm.additionalResources || [],
      assignedResources: alarm.assignedResources || [],
    };
  };

  useEffect(() => {
    const displayMatch = window.location.pathname.match(/^\/participant-live\/([a-f0-9\-]+)$/i);
    if (displayMatch && displayMatch[1]) {
      setSessionId(displayMatch[1]);
      setCurrentPage('livestream-display');
      return;
    }

    const livestreamMatch = window.location.pathname.match(/^\/livestream\/([a-f0-9\-]+)$/i);
    if (livestreamMatch && livestreamMatch[1]) {
      setSessionId(livestreamMatch[1]);
      setCurrentPage('livestream');
      return;
    }

    const joinMatch = window.location.pathname.match(/^\/join\/([a-f0-9\-]+)$/i);
    if (joinMatch && joinMatch[1]) {
      const params = new URLSearchParams(window.location.search);
      setSessionId(joinMatch[1]);
      setPendingAlarmId(params.get('alarmId'));
      setCurrentPage('standby');
      return;
    }

    const alarmMatch = window.location.pathname.match(/^\/alarm\/([a-f0-9\-]+)$/i);
    if (!alarmMatch || !alarmMatch[1]) return;

    loadAlarmContext(alarmMatch[1])
      .then(() => setCurrentPage('standby'))
      .catch((error) => {
        console.error('Error resolving alarm route:', error);
      });
  }, []);

  // Listen for incoming alarms
  useEffect(() => {
    if (!socket) return;

    socket.on('alarm:dispatch', (alarm) => {
      setCurrentAlarm(alarm);
      setCurrentPage('alarm');
      startAlarm();
    });

    socket.on('incident:updated', (payload: any) => {
      const incident = payload?.incident;
      if (!incident) return;

      setCurrentAlarm((previous: any) => {
        if (!previous) return previous;
        if (previous.id !== incident.id) return previous;
        return {
          ...previous,
          ...incident,
          location: {
            ...previous.location,
            ...(incident.location || {}),
          },
        };
      });
    });

    socket.on('unit:status-changed', (event: any) => {
      if (!event) return;
      if (unitId && event.unitId && event.unitId !== unitId) return;

      const nextStatus = String(event.status || '');
      setUnitStatus(nextStatus);

      if (nextStatus === '06' || nextStatus === 'available') {
        stopAlarm();
        setCurrentAlarm(null);
        setCurrentPage('standby');
        return;
      }

      if (currentAlarm && currentPage === 'alarm' && nextStatus && nextStatus !== '0' && nextStatus !== 'standby') {
        stopAlarm();
        setCurrentPage('enroute');
      }
    });

    socket.on('alarm:cancelled', () => {
      stopAlarm();
      setCurrentAlarm(null);
      setCurrentPage('standby');
    });

    socket.on('incident:cleared', () => {
      stopAlarm();
      setCurrentAlarm(null);
      setCurrentPage('standby');
    });

    return () => {
      socket.off('alarm:dispatch');
      socket.off('incident:updated');
      socket.off('unit:status-changed');
      socket.off('alarm:cancelled');
      socket.off('incident:cleared');
    };
  }, [socket, unitId, currentAlarm, currentPage]);

  useEffect(() => {
    return () => {
      stopAlarm();
    };
  }, []);

  const getAlarmAudio = () => {
    if (!alarmAudioRef.current) {
      const alarmAudio = new Audio('/audio/audio-alarm.m4a');
      alarmAudio.loop = true;
      alarmAudio.preload = 'auto';
      alarmAudio.load();
      alarmAudioRef.current = alarmAudio;
    }

    return alarmAudioRef.current;
  };

  const startAlarm = () => {
    try {
      const alarmAudio = getAlarmAudio();
      if (!alarmAudio.paused) {
        pendingAlarmPlaybackRef.current = false;
        return;
      }

      pendingAlarmPlaybackRef.current = true;
      void alarmAudio.play()
        .then(() => {
          pendingAlarmPlaybackRef.current = false;
        })
        .catch(() => {
          // Browser blocked autoplay. Retry after next user interaction.
        });
    } catch (error) {
      console.error('Error playing alarm sound:', error);
    }
  };

  const stopAlarm = () => {
    pendingAlarmPlaybackRef.current = false;

    if (!alarmAudioRef.current) return;
    alarmAudioRef.current.pause();
    alarmAudioRef.current.currentTime = 0;
  };

  useEffect(() => {
    const retryAlarmPlayback = () => {
      if (!pendingAlarmPlaybackRef.current) return;

      const alarmAudio = getAlarmAudio();
      alarmAudio.load();
      void alarmAudio.play()
        .then(() => {
          pendingAlarmPlaybackRef.current = false;
        })
        .catch(() => {
          // Ignore until the next user interaction.
        });
    };

    window.addEventListener('pointerdown', retryAlarmPlayback);
    window.addEventListener('keydown', retryAlarmPlayback);

    return () => {
      window.removeEventListener('pointerdown', retryAlarmPlayback);
      window.removeEventListener('keydown', retryAlarmPlayback);
    };
  }, []);

  const handleJoinSession = (scannedSessionId: string | null, scannedAlarmId?: string) => {
    if (scannedAlarmId && !scannedSessionId) {
      loadAlarmContext(scannedAlarmId)
        .then(() => setCurrentPage('standby'))
        .catch((error) => {
          console.error('Error resolving scanned alarm link:', error);
        });
      return;
    }

    setSessionId(scannedSessionId);
    setPendingAlarmId(scannedAlarmId || null);
    setCurrentPage('standby');
  };

  const handleRegistration = (name: string, crew: string) => {
    setUnitName(name);
    setCrewName(crew);
    localStorage.setItem('participantUnitName', name);
    localStorage.setItem('participantCrewName', crew);

    if (sessionId && socket) {
      socket.emit('participant:join-session', {
        sessionId,
        unitName: name,
        crewName: crew,
        deviceInfo: {
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        },
      }, (result: { success: boolean; unitId?: string; error?: string }) => {
        if (!result?.success) {
          console.error('Join session failed:', result?.error);
          return;
        }
        setUnitId(result.unitId || null);

        if (pendingAlarmId) {
          loadAlarmContext(pendingAlarmId)
            .then((alarm) => {
              setCurrentAlarm(alarm);
              setCurrentPage('alarm');
              startAlarm();
            })
            .catch((error) => {
              console.error('Error loading alarm from QR link:', error);
            });
        }
      });
    }
  };

  const handleStatusChange = (status: string) => {
    setUnitStatus(status);

    if (currentAlarm && socket && unitId) {
      socket.emit('unit:update-status', {
        alarmId: currentAlarm.id,
        status,
        latitude: 48.2,
        longitude: 15.5,
      });
    }

    if (status === 'responding') {
      stopAlarm();
    }
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-dispatch-dark text-white flex items-center justify-center overflow-y-auto">
        <div className="text-center">
          <p className="text-2xl font-bold mb-2">🔌 Verbindungsfehler</p>
          <p className="text-gray-400">Bitte Verbindung zum Server überprüfen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dispatch-dark text-white overflow-y-auto">
      {currentPage === 'scanner' ? (
        <QRScanner onScan={handleJoinSession} />
      ) : currentPage === 'livestream-display' ? (
        <LivestreamDisplay socket={socket} sessionId={sessionId} />
      ) : currentPage === 'livestream' ? (
        <LivestreamPublisher socket={socket} sessionId={sessionId} />
      ) : currentPage === 'standby' ? (
        <Standby
          sessionId={sessionId}
          unitName={unitName}
          crewName={crewName}
          onRegister={handleRegistration}
          socket={socket}
        />
      ) : currentPage === 'alarm' ? (
        <AlarmReceived
          alarm={currentAlarm}
          unitName={unitName}
          unitStatus={unitStatus}
          onStatusChange={handleStatusChange}
          onEnroute={() => {
            stopAlarm();
            setCurrentPage('enroute');
          }}
        />
      ) : (
        <EnrouteView
          alarm={currentAlarm}
          unitName={unitName}
          unitStatus={unitStatus}
          onStatusChange={handleStatusChange}
          onComplete={() => {
            setCurrentPage('standby');
            setCurrentAlarm(null);
          }}
        />
      )}
    </div>
  );
}
